import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getFeaturedReferences } from '@/data/references';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

const References = () => {
  const featuredReferences = getFeaturedReferences();
  const imageSizes = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';

  return (
    <section id="referenzen" className="py-24 md:py-32 relative bg-background">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">Referenzen</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-foreground">
            Welche Projekte haben wir
            <span className="gradient-text"> bereits umgesetzt?</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Ein Auszug unserer erfolgreich umgesetzten Webprojekte – 
            jedes einzelne mit Liebe zum Detail entwickelt.
          </p>
        </motion.div>

        {/* References Grid - Only 3 Featured */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredReferences.map((ref, index) => (
            <motion.div
              key={ref.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              whileHover={{ y: -10 }}
              className="group block"
            >
              <Link
                to={`/referenzen/${ref.id}`}
                className="block h-full"
              >
                <div className="relative rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/50 transition-all duration-300 h-full shadow-lg hover:shadow-xl">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={buildSrc(ref.image, 1024)}
                      srcSet={buildSrcSet(ref.image, IMAGE_WIDTHS.reference)}
                      sizes={imageSizes}
                      alt={ref.title}
                      loading="lazy"
                      decoding="async"
                      width={408}
                      height={230}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-white/90 text-xs font-medium text-card-foreground shadow-sm">
                        {ref.category}
                      </span>
                    </div>

                    {/* Arrow Icon */}
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                      <ArrowRight className="w-5 h-5 text-card-foreground" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-display font-semibold mb-2 group-hover:text-primary transition-colors text-card-foreground">
                      {ref.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                      {ref.description}
                    </p>
                    <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                      Case Study lesen
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>



        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/referenzen"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
          >
            Alle Referenzen ansehen
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default References;
